// ============================================================================
// COURT MASTER SCREEN — MSTR-COURT-001
// ============================================================================
// Court Type is now a FK to mst.tbl_court_type (no longer hardcoded enum).
// Court Place FK uses seedOptions cascade for inactive parents.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useCourts } from 'hooks/api/useCourts';
import { useCourtTypes } from 'hooks/api/useCourtTypes';
import { useCourtPlaces } from 'hooks/api/useCourtPlaces';
import { courtService } from 'api/services/courtService';
import type { Court, CourtSearchRequest } from 'api/types/court.types';
import CourtFormModal, {
  type CourtTypeOption,
  type CourtPlaceOption,
} from 'components/modals/modules/court-module/CourtFormModal';

type ViewMode = 'list' | 'grid';

interface CourtMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const CourtMasterScreen: React.FC<CourtMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<CourtSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useCourts(queryParams);

  // Court Type — active for form, all for filter + seed.
  const { data: courtTypes, refetch: refetchTypes } = useCourtTypes({ page: 1, pageSize: 100 });
  const { data: allCourtTypes, refetch: refetchAllTypes } = useCourtTypes({ page: 1, pageSize: 100, statusCode: 'ALL' });
  // Court Place — active for form, all for filter + seed.
  const { data: places, refetch: refetchPlaces } = useCourtPlaces({ page: 1, pageSize: 200 });
  const { data: allPlaces, refetch: refetchAllPlaces } = useCourtPlaces({ page: 1, pageSize: 200, statusCode: 'ALL' });

  const courtTypeOptions: CourtTypeOption[] = useMemo(
    () => (courtTypes ?? []).map(t => ({ value: t.id, label: `${t.typeCode} — ${t.typeName}` })),
    [courtTypes]
  );

  const courtTypeFilterOptions = useMemo(() => {
    const list = (allCourtTypes ?? []).map(t => {
      let suffix = '';
      if (t.deletedAt) suffix = ' (Deleted)';
      else if (t.statusCode && t.statusCode !== 'ACTIVE') suffix = ` (${t.statusCode})`;
      return { value: t.id, label: `${t.typeCode} — ${t.typeName}${suffix}` };
    });
    return [{ value: '', label: 'All types' }, ...list];
  }, [allCourtTypes]);

  const courtPlaceOptions: CourtPlaceOption[] = useMemo(
    () => (places ?? []).map(p => ({
      value: p.id,
      label: `${p.placeCode} — ${p.placeName}${p.stateProvinceCode ? ` · ${p.stateProvinceCode}` : ''}`,
    })),
    [places]
  );

  const courtPlaceFilterOptions = useMemo(() => {
    const list = (allPlaces ?? []).map(p => {
      let suffix = '';
      if (p.deletedAt) suffix = ' (Deleted)';
      else if (p.statusCode && p.statusCode !== 'ACTIVE') suffix = ` (${p.statusCode})`;
      return {
        value: p.id,
        label: `${p.placeCode} — ${p.placeName}${p.stateProvinceCode ? ` · ${p.stateProvinceCode}` : ''}${suffix}`,
      };
    });
    return [{ value: '', label: 'All places' }, ...list];
  }, [allPlaces]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [courtCodeFilter, setCourtCodeFilter] = useState('');
  const [courtNameFilter, setCourtNameFilter] = useState('');
  const [courtTypeIdFilter, setCourtTypeIdFilter] = useState<string>('');
  const [placeFilter, setPlaceFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(p => ({ ...p, searchTerm: searchQuery.trim() || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selectedCourt, setSelectedCourt] = useState<Court | null>(null);

  // Seed option for inactive Court Type on edit
  const seedCourtTypeOptions: CourtTypeOption[] = useMemo(() => {
    if (formMode !== 'edit' || !selectedCourt) return [];
    if (courtTypeOptions.some(o => o.value === selectedCourt.courtTypeId)) return [];
    const fromAll = (allCourtTypes ?? []).find(t => t.id === selectedCourt.courtTypeId);
    let suffix = ' (Inactive)';
    if (fromAll) {
      if (fromAll.deletedAt) suffix = ' (Deleted)';
      else if (fromAll.statusCode && fromAll.statusCode !== 'ACTIVE') suffix = ` (${fromAll.statusCode})`;
    }
    return [{
      value: selectedCourt.courtTypeId,
      label: `${selectedCourt.courtTypeCode ?? '?'} — ${selectedCourt.courtTypeName ?? 'Unknown'}${suffix}`,
    }];
  }, [formMode, selectedCourt, courtTypeOptions, allCourtTypes]);

  // Seed option for inactive Court Place on edit
  const seedCourtPlaceOptions: CourtPlaceOption[] = useMemo(() => {
    if (formMode !== 'edit' || !selectedCourt || !selectedCourt.courtPlaceId) return [];
    const placeId = selectedCourt.courtPlaceId;
    if (courtPlaceOptions.some(o => o.value === placeId)) return [];
    const parentFromAll = (allPlaces ?? []).find(p => p.id === placeId);
    let suffix = ' (Inactive)';
    if (parentFromAll) {
      if (parentFromAll.deletedAt) suffix = ' (Deleted)';
      else if (parentFromAll.statusCode && parentFromAll.statusCode !== 'ACTIVE') suffix = ` (${parentFromAll.statusCode})`;
      else suffix = '';
    }
    const stateSuffix = selectedCourt.stateProvinceCode ? ` · ${selectedCourt.stateProvinceCode}` : '';
    return [{
      value: placeId,
      label: `${selectedCourt.placeCode ?? '?'} — ${selectedCourt.placeName ?? 'Unknown'}${stateSuffix}${suffix}`,
    }];
  }, [formMode, selectedCourt, courtPlaceOptions, allPlaces]);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      refetchTypes(); refetchAllTypes();
      refetchPlaces(); refetchAllPlaces();
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(c => c.courtCode === recordCode);
        if (rec) setSelectedCourt(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data, refetchTypes, refetchAllTypes, refetchPlaces, refetchAllPlaces]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [courtToDelete, setCourtToDelete] = useState<Court | null>(null);

  const handleAddNew = useCallback(() => {
    refetchTypes(); refetchAllTypes();
    refetchPlaces(); refetchAllPlaces();
    if (formModalOpen) formModalMinimizeRef.current?.();
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedCourt(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen, refetchTypes, refetchAllTypes, refetchPlaces, refetchAllPlaces]);

  const handleEdit = useCallback(
    (court: Court) => {
      refetchTypes(); refetchAllTypes();
      refetchPlaces(); refetchAllPlaces();
      const modalId = tabId ? `${tabId}-form-modal-edit-${court.courtCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedCourt(court);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal, refetchTypes, refetchAllTypes, refetchPlaces, refetchAllPlaces]
  );

  const handleView = useCallback(
    (court: Court) => {
      const modalId = tabId ? `${tabId}-view-modal-${court.courtCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedCourt(court);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (court: Court) => {
      setSelectedCourt(court);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((c: Court) => { setCourtToDelete(c); setDeleteModalOpen(true); }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!courtToDelete) return;
    setIsDeleting(true);
    try {
      await courtService.delete(courtToDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Court "${courtToDelete.courtCode}" deleted successfully.`);
      setCourtToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [courtToDelete, refetch]);

  const handleSaved = useCallback(() => {
    refetch(); refetchTypes(); refetchAllTypes(); refetchPlaces(); refetchAllPlaces();
  }, [refetch, refetchTypes, refetchAllTypes, refetchPlaces, refetchAllPlaces]);

  const handleRefresh = useCallback(() => {
    refetch(); refetchTypes(); refetchAllTypes(); refetchPlaces(); refetchAllPlaces();
  }, [refetch, refetchTypes, refetchAllTypes, refetchPlaces, refetchAllPlaces]);

  const handlePageChange = useCallback((i: number) => setQueryParams(p => ({ ...p, page: i + 1 })), []);
  const handlePageSizeChange = useCallback((s: number) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 })), []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(p => ({
      ...p,
      courtCode: courtCodeFilter.trim() || undefined,
      courtName: courtNameFilter.trim() || undefined,
      courtTypeId: courtTypeIdFilter || undefined,
      courtPlaceId: placeFilter || undefined,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [courtCodeFilter, courtNameFilter, courtTypeIdFilter, placeFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setCourtCodeFilter(''); setCourtNameFilter(''); setCourtTypeIdFilter(''); setPlaceFilter(''); setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (queryParams.courtCode) c++;
    if (queryParams.courtName) c++;
    if (queryParams.courtTypeId) c++;
    if (queryParams.courtPlaceId) c++;
    if (queryParams.statusCode) c++;
    return c;
  }, [queryParams.courtCode, queryParams.courtName, queryParams.courtTypeId, queryParams.courtPlaceId, queryParams.statusCode]);

  const columns: SyutGridColumn<Court>[] = useMemo(() => [
    { id: 'courtCode', header: 'Court Code', accessorKey: 'courtCode', size: 150, enableSorting: true },
    { id: 'courtName', header: 'Court Name', accessorKey: 'courtName', size: 260, enableSorting: true, flex: true },
    {
      id: 'courtTypeName', header: 'Type', accessorKey: 'courtTypeName', size: 160, enableSorting: true,
      cell: ({ row }) => {
        const t = row.original.courtTypeName ?? row.original.courtTypeCode ?? '-';
        return (
          <span style={{
            fontSize: '0.75rem', fontWeight: 600, color: '#2563eb',
            padding: '2px 8px', border: '1px solid #2563eb', borderRadius: '999px',
          }}>{t}</span>
        );
      },
    },
    {
      id: 'placeName', header: 'Place', accessorKey: 'placeName', size: 160, enableSorting: true,
      cell: ({ row }) => row.original.placeName ?? row.original.placeCode ?? '-',
    },
    {
      id: 'stateProvinceName', header: 'State', accessorKey: 'stateProvinceName', size: 140, enableSorting: true,
      cell: ({ row }) => row.original.stateProvinceName ?? row.original.stateProvinceCode ?? '-',
    },
    {
      id: 'statusCode', header: 'Status', accessorKey: 'statusCode', size: 110, enableSorting: true,
      cell: ({ row }) => {
        const c = row.original;
        const code = c.deletedAt ? 'DELETED' : (c.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span style={{
            fontSize: '0.75rem', fontWeight: 600, color,
            padding: '2px 8px', border: `1px solid ${color}`, borderRadius: '999px',
          }} title={c.deletedAt ? `Deleted at ${new Date(c.deletedAt).toLocaleString()}` : undefined}>
            {code}
          </span>
        );
      },
    },
    {
      id: 'updatedAt', header: 'Last Updated', accessorKey: 'updatedAt', size: 150, enableSorting: true,
      cell: ({ row }) => {
        const v = row.original.updatedAt;
        if (!v) return '-';
        try { return new Date(v).toLocaleString(); } catch { return v; }
      },
    },
  ], []);

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  const getRowActions = useCallback((c: Court): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(c) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(c) },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(c) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(c), variant: 'danger' },
  ], [handleEdit, handleView, handleCopy, handleDeleteClick]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add', tooltip: 'Add New Court', onClick: handleAddNew, variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', tooltip: 'Refresh', onClick: handleRefresh, variant: 'icon' },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Court Master"
        description="Courts, tribunals, and arbitration venues where cases are filed. Type and Place are FK references to master tables."
        icon={<IconSystem.file size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search code, name, type, place...' }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (m) => setViewMode(m as ViewMode),
        }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen || activeFilterCount > 0, activeFilterCount }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No courts found"
              />
            )}

            {viewMode === 'grid' && (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div style={{
                  padding: '1rem', display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
                }}>
                  {data.map((c) => (
                    <div key={String(c.id)}
                      style={{ position: 'relative', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1rem', background: 'var(--bg-primary)', cursor: 'pointer' }}
                      onClick={() => handleEdit(c)}>
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(c)} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, paddingRight: '1.5rem' }}>
                        {c.courtCode} · <span style={{ color: '#2563eb' }}>{c.courtTypeName ?? c.courtTypeCode}</span>
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>
                        {c.courtName}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                        {c.placeName ?? c.placeCode ?? '—'}{c.stateProvinceName ? ` · ${c.stateProvinceName}` : ''}
                      </div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: c.statusCode === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                        {c.statusCode || 'ACTIVE'}
                      </div>
                    </div>
                  ))}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={queryParams.pageSize ?? 25}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                        <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange((queryParams.page ?? 1) - 2)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>
                        Page {queryParams.page ?? 1} of {Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(queryParams.page ?? 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25))) - 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                        <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Court Code</label>
              <input type="text" className="form-input" value={courtCodeFilter}
                onChange={(e) => setCourtCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Court Name</label>
              <input type="text" className="form-input" value={courtNameFilter}
                onChange={(e) => setCourtNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Court Type"
                options={courtTypeFilterOptions}
                value={courtTypeIdFilter}
                onChange={(v) => setCourtTypeIdFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Court Place"
                options={courtPlaceFilterOptions}
                value={placeFilter}
                onChange={(v) => setPlaceFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED', label: 'Deleted only' },
                  { value: 'ALL', label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={courtToDelete ? `Are you sure you want to delete court "${courtToDelete.courtCode}"? This action cannot be undone.` : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => { if (!isDeleting) { setDeleteModalOpen(false); setCourtToDelete(null); } }}
      />

      <CourtFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedCourt}
        courtTypeOptions={courtTypeOptions}
        seedCourtTypeOptions={seedCourtTypeOptions}
        courtPlaceOptions={courtPlaceOptions}
        seedCourtPlaceOptions={seedCourtPlaceOptions}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default CourtMasterScreen;
